/**
 * The four ETA state changes — cancel, reject, decline a cancellation, decline a rejection — as pure
 * rules (Block 11, BLOCK-11-PLAN §7.1, §7.3; D-2, D-8, D-14, TD-4, TD-13, TD-15; R2-01).
 *
 * `stateChangeRefusal` is the ONE predicate behind `allowed` in `GET …/eta`, the command's
 * pre-check, the worker's send-time re-check and the batch page's `eligibleFor=cancel` (D-2). The
 * ETA facts it rests on are the official pages recorded in `docs/eta/sdk-notes/2026-10-05-*.md`;
 * every temporary decision sits behind one named function here (DEC-052, DEC-060…DEC-063).
 */
export const STATE_CHANGE_ACTIONS = [
    'cancel',
    'reject',
    'decline_cancellation',
    'decline_rejection',
];
/** Who takes each action (the official pages: cancel and decline-rejection are the issuer's). */
export const ACTION_ROLE = {
    cancel: 'issuer',
    reject: 'receiver',
    decline_cancellation: 'receiver',
    decline_rejection: 'issuer',
};
/** TD-15 (DEC-052): cancel is offered while the ETA state is `submitted` or `valid`; ETA decides. */
export const CANCELLABLE_ETA_STATES = ['submitted', 'valid'];
/** In flight: it blocks every action on the document (N-04). */
export const IN_FLIGHT_STATUSES = [
    'queued',
    'claimed',
    'sending',
    'unknown',
];
/** Taken by ETA but not confirmed (while `confirmedAt` is null): it blocks its own action only. */
export const UNCONFIRMED_STATUSES = [
    'done',
    'done_unconfirmed',
];
export const isInFlight = (r) => IN_FLIGHT_STATUSES.includes(r.status);
export const isUnconfirmed = (r) => UNCONFIRMED_STATUSES.includes(r.status) && r.confirmedAt === null;
/** Live: in flight, or taken and not confirmed. `released`, `refused`, `not_applied`, `failed` are not. */
export const isLiveRequest = (r) => isInFlight(r) || isUnconfirmed(r);
/**
 * `cancelPending(state)` (§7.1, N-02, R2-01; DEC-061): true when a live cancel request exists, **or**
 * ETA reports `cancelRequestDate` with no `declineCancelRequestDate` and the status is not
 * `cancelled`. Once the buyer declined our cancellation, the invoice is no longer pending
 * cancellation, and a credit note can reference it (NR-N15). The only rule behind
 * `references.N:cancellation_pending`, note-source eligibility and `stateChangeRefusal`.
 */
export function cancelPending(state) {
    if (state.requests.some((r) => r.action === 'cancel' && isLiveRequest(r)))
        return true;
    return (!!state.cancelRequestDate && !state.declineCancelRequestDate && state.etaStatus !== 'cancelled');
}
/**
 * TD-13 (U-59; Q13, Q16): what a freeze does to an action. Cancel is blocked at scope 1 (full) or 2
 * (cancellation); reject at scope 1; the two declines are only warned (no freeze error is listed on
 * their pages; ETA decides, and its refusal is mapped). A freeze whose scope is not set is warned.
 */
export function isFrozenFor(action, freeze) {
    if (!freeze || freeze.frozen !== true)
        return null;
    if (action === 'cancel' && (freeze.scope === 1 || freeze.scope === 2))
        return 'blocked';
    if (action === 'reject' && freeze.scope === 1)
        return 'blocked';
    return 'warned';
}
/**
 * Why an action is refused locally, or `null` when it may be requested (in the style of
 * `supersedeRefusal`). Checks run from the request book to the ETA state to the window:
 * 1. another request in flight blocks every action (`request_in_flight`, N-04);
 * 2. the same action taken but unconfirmed blocks a repeat (`already_requested`, N-04, R2-03);
 * 3. the state the action needs (F-01…F-04; TD-15), each decline once, and no second cancel after
 *    the receiver declined one (TD-4, DEC-063: a credit note remains, F-10);
 * 4. live notes referencing the invoice (D-13, F-17);
 * 5. a freeze that blocks the action (TD-13);
 * 6. a window certainly closed (D-1, TD-2): `uncertain` and `unknown` are allowed, ETA decides.
 */
export function stateChangeRefusal(input) {
    const { action } = input;
    if (input.requests.some(isInFlight))
        return 'request_in_flight';
    if (input.requests.some((r) => r.action === action && isUnconfirmed(r)))
        return 'already_requested';
    const status = input.etaStatus ?? null;
    switch (action) {
        case 'cancel':
            if (!status || !CANCELLABLE_ETA_STATES.includes(status))
                return 'not_cancellable';
            if (input.declineCancelRequestDate)
                return 'cancel_declined_by_receiver';
            if (cancelPending(input))
                return 'already_requested';
            if (input.liveNotes && input.liveNotes.length > 0)
                return 'active_referencing_documents';
            break;
        case 'reject':
            // "Rejection action for any document can happen only once"; only a valid document.
            if (status !== 'valid' || input.rejectRequestDate)
                return 'not_rejectable';
            break;
        case 'decline_cancellation':
            if (!input.cancelRequestDate || (status !== 'cancelled' && status !== 'valid'))
                return 'not_cancelled';
            if (input.declineCancelRequestDate)
                return 'already_declined';
            break;
        case 'decline_rejection':
            if (!input.rejectRequestDate || (status !== 'rejected' && status !== 'valid'))
                return 'not_rejected';
            if (input.declineRejectRequestDate)
                return 'already_declined';
            break;
    }
    if (isFrozenFor(action, input.freeze) === 'blocked')
        return 'document_frozen';
    if (input.windowState === 'closed')
        return 'eta_window_closed';
    return null;
}
/** The API error code of each local refusal (§7.3). */
export function refusalCode(reason) {
    if (reason === 'eta_window_closed')
        return 'eta_window_closed';
    if (reason === 'active_referencing_documents')
        return 'active_referencing_documents';
    if (reason === 'document_frozen')
        return 'document_frozen';
    return 'document_state_conflict';
}
/**
 * Confirmation (D-2, N-04; DEC-060): a request is confirmed when Get Document Details shows its own
 * field — `cancelRequestDate`, `rejectRequestDate`, `declineCancelRequestDate`,
 * `declineRejectRequestDate` — or a status that reflects it: `cancelled` for a cancel, `rejected`
 * for a reject, and `valid` for a decline made while the document was cancelled or rejected.
 */
export function confirmsRequest(action, details, etaStatusAtRequest) {
    switch (action) {
        case 'cancel':
            return !!details.cancelRequestDate || details.etaStatus === 'cancelled';
        case 'reject':
            return !!details.rejectRequestDate || details.etaStatus === 'rejected';
        case 'decline_cancellation':
            return (!!details.declineCancelRequestDate ||
                (etaStatusAtRequest === 'cancelled' && details.etaStatus === 'valid'));
        case 'decline_rejection':
            return (!!details.declineRejectRequestDate ||
                (etaStatusAtRequest === 'rejected' && details.etaStatus === 'valid'));
    }
}
/**
 * R2-01 (DEC-062): may this request be released? Only a cancel that ETA took and never confirmed.
 * `release_not_allowed` otherwise.
 */
export function releaseRefusal(request) {
    return request.action === 'cancel' &&
        request.status === 'done_unconfirmed' &&
        request.confirmedAt === null
        ? null
        : 'release_not_allowed';
}
/**
 * R2-01 (DEC-062): what the worker's fresh Get Document Details read decides for a release.
 * - `released` only when the read shows `valid`, no `cancelRequestDate`, and not `cancelled`;
 * - `confirmed` when the read shows the request's date or `cancelled` (ETA did apply it);
 * - `refused` (`release_not_clean`) for anything else.
 */
export function releaseDecision(details) {
    if (details.cancelRequestDate || details.etaStatus === 'cancelled')
        return 'confirmed';
    if (details.etaStatus === 'valid')
        return 'released';
    return 'refused';
}
/** Our bound on a reason (D-15, TD-14): 1–500 characters with some non-blank content. */
export const STATE_CHANGE_REASON_MAX = 500;
export function reasonIssue(reason) {
    if (typeof reason !== 'string' || reason.trim() === '')
        return 'reason_required';
    if ([...reason].length > STATE_CHANGE_REASON_MAX)
        return 'reason_too_long';
    return null;
}
//# sourceMappingURL=state-change.js.map